import type {
  Journey,
  StepState,
  StepExtended,
  Step,
  ValueError
} from '@epilot/journey-logic-commons'
import { isBlockValid, next } from '@epilot/journey-logic-commons'
import { isBlockFact } from '@epilot/journey-logic-commons/src/next/references'
import type { JsonFormsCore } from '@jsonforms/core'
import isEqual from 'fast-deep-equal/react'

import type { DesiredState } from '../../store/types'
import { DefaultEpilotTheme, type EpilotTheme } from '../../types'

const defaultBreakpoints: EpilotTheme['breakpoints'] = {
  values: { xs: 0, sm: 600, md: 960, lg: 1200, xl: 1536 }
}
const defaultTheme: EpilotTheme = {
  ...structuredClone(DefaultEpilotTheme),
  spacing: 4,
  breakpoints: defaultBreakpoints
}

export const transformJourneyTheme = (
  activeJourneyTheme?: NonNullable<Journey['design']>['theme']
) =>
  activeJourneyTheme
    ? ({
        ...structuredClone(activeJourneyTheme),
        breakpoints: defaultBreakpoints
      } as EpilotTheme)
    : defaultTheme

export const getValueErrors = (
  value: Pick<JsonFormsCore, 'data' | 'errors'>,
  currentStepIndex: number,
  newState: StepState,
  currentStep: Step
) => {
  // check each block value for _isValid
  const blockErrors: ValueError[] = computeBlockErrors(
    { ...currentStep, stepIndex: currentStepIndex },
    newState
  )

  return [...(value.errors ?? []), ...blockErrors]
}

const computeBlockErrors = (
  step: StepExtended,
  state: StepState
): ValueError[] => {
  if (!state) {
    return []
  }

  return Object.keys(state)
    .filter((blockName) => !isBlockValid(step, state, blockName))
    .map<ValueError>((blockName) => ({
      instancePath: `/${blockName}`,
      message: 'Not valid'
    }))
}

export function getWhichBlockHasChanged(
  oldStat?: Record<string, unknown>,
  newStat?: Record<string, unknown>
) {
  if (!newStat) {
    return undefined
  }
  const newKeys = Object.keys(newStat)
  const oldKeys = oldStat ? Object.keys(oldStat) : []

  if (!oldStat) {
    return newKeys
  }

  return newKeys
    .filter((key) => !isEqual(newStat[key], oldStat[key]))
    .concat(oldKeys.filter((key) => !(key in newStat)))
}

/**
 * this function would clean up the required array from blocks mentioned there that are not referenced in the uischema
 * @param rawStep the step to clean up
 * @returns cleaned step
 */
export function protectAgainstRequiredNoUischema(
  uischema: StepExtended['uischema'],
  blockesRequired?: string[]
) {
  // const step = structuredClone(rawStep)
  // const blockesRequired = step.schema?.required
  let cleanedArray: string[] = []

  if (blockesRequired && blockesRequired.length > 0) {
    const uischemaStr = JSON.stringify(uischema)

    // filter any block which does not exists in uischema
    cleanedArray = blockesRequired.filter((blockName) =>
      uischemaStr.includes(`#/properties/${blockName}`)
    )
  }

  return cleanedArray
}
interface DisplayConditionsStatus {
  [block: string]: {
    display: boolean
    trigger: string
  }
}

export const computeDisplayConditionsStatus = (
  journey: next.Journey,
  shownBlocks: DesiredState['show']
): DisplayConditionsStatus => {
  const displayConditionsStatus: DisplayConditionsStatus = {}
  const blockIds = Object.keys(journey.blocks ?? {})

  blockIds.forEach((blockId) => {
    const isBlockShown = shownBlocks.has(blockId)
    let triggerBlockId: string = ''

    // Compute a map of target blocks to the display condition logics that hide them
    // This is to avoid nested loops in the following logic
    const displayConditionReferences = getDisplayConditionLogicsForTargetBlocks(
      journey.logics
    )

    if (!isBlockShown) {
      // The block is hidden, find the logic that hides it
      const relatedLogicId = displayConditionReferences[blockId]

      if (!relatedLogicId) {
        // The block should not be hidden with logics, we cannot compute the display conditions status
        console.error(
          `Block ${blockId} should not be hidden as there is no related display conditions logic`
        )

        return
      }

      const relatedLogic = journey.logics?.[relatedLogicId]

      if (!relatedLogic) {
        // The logic should exist, we cannot compute the display conditions status
        console.error(
          `Logic ${relatedLogicId} should exist as it is related to block ${blockId}`
        )

        return
      }

      // Ensure that the block is hidden by a block fact
      const fact = relatedLogic.conditions[0][0].fact

      if (!isBlockFact(fact)) {
        return
      }

      triggerBlockId = fact.referenceId

      // Add the block to the displayConditionsStatus as it's hidden
      // Use empty string as the trigger if we don't know the trigger as a block (example: context parameter)
      displayConditionsStatus[getBlockPathByBlockId(blockId, journey)] = {
        display: isBlockShown,
        trigger: getBlockPathByBlockId(triggerBlockId, journey) ?? ''
      }
    }
  })

  return displayConditionsStatus
}

/**
 * Returns a map of target blocks to the display condition logics that hide them.
 *
 * @param logics - logics in v4 format
 * @returns `{ block1: displayCondition1, block2: displayCondition2 }`
 * where `block1` and `block2` are the ids of the blocks that are
 * hidden by the display condition logics `displayCondition1` and
 * `displayCondition2` respectively.
 */
const getDisplayConditionLogicsForTargetBlocks = (
  logics: Record<string, next.Logic> | undefined
) => {
  if (!logics) {
    return {}
  }

  const displayConditionLogics: Record<string, next.LogicId> = {}

  Object.keys(logics).forEach((logicId) => {
    const logic = logics[logicId]

    if (logic.action === next.LOGIC_ACTION.DISPLAY_CONDITION) {
      const targetBlock = logic.settings.targetBlock

      if (targetBlock) {
        displayConditionLogics[targetBlock] = logicId
      }
    }
  })

  return displayConditionLogics
}

/**
 * Resolve block path in the old format `stepIdx/blockName` using blockIds.
 *
 * @param blockId - UUID of the block
 * @param journey - journey in v4 format
 * @returns the path of the block in the old format `stepIdx/blockName`
 */
const getBlockPathByBlockId = (blockId: string, journey: next.Journey) => {
  if (!journey.blocks || !journey.blocks[blockId]) {
    throw new Error(`Block ${blockId} not found in journey`)
  }

  const block = journey.blocks[blockId]
  const stepId = block.stepId
  const blockName = block.name

  const stepIdx = journey.steps[stepId].orderIndex

  return `${stepIdx}/${blockName}`
}
